import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Global from '../../../store/global-selectors';
import { InfoIcon } from 'ui-components/components/info-icon';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexRow } from 'ui-components/components/flex';
import { Button } from 'ui-components/components/button';
import { GROUP_IDS } from '../constants';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { CswEvents, useTrackRateShopperEvent } from '../../../pages/settings/rate-shopper/hooks/use-track-rate-shopper-analytic-events';
import { pages } from '../../../pages/settings/nav-items';
import { messages } from './messages';
import style from './style.module.less';

const useGetPathToRateShopperSettingsPage = (pathname) => {
  const RATE_SHOPPER_SETTINGS_PAGE = pages.serviceCollections.location;
  const SETTING_PAGE = pages.settings.location;
  const isOnSettingsPage = pathname.includes(SETTING_PAGE);

  const NavigateTo = isOnSettingsPage ? `${RATE_SHOPPER_SETTINGS_PAGE}` : `/${SETTING_PAGE}/${RATE_SHOPPER_SETTINGS_PAGE}`;

  return NavigateTo;
};

const ManageRateShopperButton = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const rateShopperSettingsPage = useGetPathToRateShopperSettingsPage(pathname);

  const trackManageClick = useTrackRateShopperEvent(CswEvents.RATE_SHOPPER_MANAGE_CLICK, { location: pathname });

  const navigateToRateRuleCreation = useCallback(() => {
    trackManageClick();
    navigate(rateShopperSettingsPage);
  }, [navigate, rateShopperSettingsPage, trackManageClick]);

  return (
    <Button className={style['manage-button']} onClick={navigateToRateRuleCreation} theme='link'>
      <FormattedMessage {...messages.manageRateShopperButton} />
      <FontAwesomeIcon icon={faArrowRight} />
    </Button>
  );
};

const RateShopperInfoIcon = () => (
  <InfoIcon
    className={style['info-icon']}
    color={THEME_CONSTANTS.unstyled}
    size='small'
    tooltipText={<FormattedMessage {...messages.rateShopperSummary} />}
  />
);

const filterByAccess = (unfilteredServiceCollections, hasRateShopperPremium) => {
  const activeShoppers = unfilteredServiceCollections.filter((s) => s.isActive);
  if (hasRateShopperPremium) return activeShoppers;

  const activeDefaultShoppers = activeShoppers.filter((s) => s.isTemplate);
  return activeDefaultShoppers;
};

const getRateShopperGroupHeaderClassName = (hasRateShopperPremium, isForSearchableDropdown) => {
  if (hasRateShopperPremium) {
    return;
  }

  return isForSearchableDropdown ? style['rate-shopper-group-header'] : style['service-collection-group'];
};

export const useServiceCollectionGroup = (selectedItem, isForSearchableDropdown = false) => {
  const unfilteredServiceCollections = useSelector(Global.selectSellerServiceCollections);
  const hasRateShopperPremium = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.PREMIUM);

  const serviceCollections = useMemo(
    () => filterByAccess(unfilteredServiceCollections, hasRateShopperPremium),
    [hasRateShopperPremium, unfilteredServiceCollections],
  );

  const items = useMemo(() => {
    return serviceCollections.map((s) => ({
      ...s,
      selected: s.serviceCollectionGuid === selectedItem?.serviceCollectionGuid,
    }));
  }, [serviceCollections, selectedItem?.serviceCollectionGuid]);

  const groupHeader = messages.rateShopperGroupName;

  const serviceCollectionGroup = useMemo(() => {
    const groupNameWithHelpLink = {
      ...groupHeader,
      values: {
        l: (chunks) => (
          <FlexRow className={style['group-with-help']}>
            <FlexRow className={style['group-label']}>
              {chunks}
              {!hasRateShopperPremium && <RateShopperInfoIcon />}
            </FlexRow>
            <FlexRow className={style['button-row']}>
              <ManageRateShopperButton />
            </FlexRow>
          </FlexRow>
        ),
      },
    };

    return {
      groupId: GROUP_IDS.SERVICE_COLLECTIONS,
      name: groupNameWithHelpLink,
      items,
      groupClassname: getRateShopperGroupHeaderClassName(hasRateShopperPremium, isForSearchableDropdown),
    };
  }, [groupHeader, hasRateShopperPremium, isForSearchableDropdown, items]);

  return serviceCollectionGroup;
};
